import Vue from 'vue'
import { getAuth } from 'firebase/auth'
import { getFirestore, doc, onSnapshot } from 'firebase/firestore'
import { subscribeAuthBootstrap } from '@/services/authBootstrap'
import {
  ROLES,
  isBasicRole,
  emptyRolesDoc,
} from '@/permissions/perms'

const MAX_RETRIES = 4
const RETRY_BASE_MS = 300

const permissionsState = Vue.observable({
  loaded: false,
  uid: null,
  permissions: emptyRolesDoc(),
  error: null,
})

let started = false
let unsubscribeBootstrap = null
let unsubscribeListener = null
let generation = 0

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function clearListener() {
  if (typeof unsubscribeListener === 'function') {
    try { unsubscribeListener() } catch (e) { /* already torn down */ }
  }
  unsubscribeListener = null
}

function resetState() {
  permissionsState.loaded = true
  permissionsState.uid = null
  permissionsState.permissions = emptyRolesDoc()
  permissionsState.error = null
}

function attach(uid) {
  clearListener()

  const gen = ++generation
  permissionsState.uid = uid
  permissionsState.permissions = emptyRolesDoc()
  permissionsState.loaded = false
  permissionsState.error = null

  let retries = 0
  const ref = doc(getFirestore(), 'permissions', uid)

  const subscribe = () => {
    if (gen !== generation) return

    unsubscribeListener = onSnapshot(
      ref,
      (snap) => {
        if (gen !== generation) return
        retries = 0

        const data = snap.exists() && snap.data() ? snap.data() : {}
        permissionsState.permissions = ROLES.reduce((acc, key) => {
          acc[key] = !!data[key]
          return acc
        }, {})
        permissionsState.loaded = true
        permissionsState.error = null
      },
      (err) => {
        if (gen !== generation) return
        console.warn('[usePermissions] listener error:', err?.code || err)
        clearListener()

        // Publishing an empty role set on a transient failure silently strips the
        // whole admin menu, so retry with a fresh token before giving up.
        const isRetryable = err?.code === 'permission-denied' || err?.code === 'unavailable'
        if (isRetryable && retries < MAX_RETRIES) {
          retries += 1
          permissionsState.loaded = false
          permissionsState.error = null

          const authUser = getAuth().currentUser
          const refresh = authUser && authUser.uid === uid
            ? authUser.getIdToken(true).catch(() => {})
            : Promise.resolve()

          refresh
            .then(() => sleep(RETRY_BASE_MS * retries))
            .then(subscribe)
          return
        }

        permissionsState.permissions = emptyRolesDoc()
        permissionsState.loaded = true
        permissionsState.error = err
      }
    )
  }

  subscribe()
}

export function startPermissionsListener() {
  if (started) return
  started = true

  // Same reasoning as useEmployee: attach only once the auth bootstrap has
  // refreshed and fenced the ID token, so the first read isn't denied.
  unsubscribeBootstrap = subscribeAuthBootstrap((payload) => {
    const authUser = payload?.authUser || null

    if (!authUser) {
      clearListener()
      generation += 1
      resetState()
      return
    }

    if (permissionsState.uid === authUser.uid && unsubscribeListener) return
    attach(authUser.uid)
  })
}

export function stopPermissionsListener() {
  clearListener()
  generation += 1
  if (typeof unsubscribeBootstrap === 'function') {
    try { unsubscribeBootstrap() } catch (e) { /* already torn down */ }
  }
  unsubscribeBootstrap = null
  started = false
  resetState()
}

export function usePermissions() {
  startPermissionsListener()

  const hasRole = (roleKey) => {
    if (isBasicRole(roleKey)) return true
    if (!ROLES.includes(roleKey)) return false
    if (!permissionsState.loaded) return false
    return !!permissionsState.permissions?.[roleKey]
  }

  return {
    state: permissionsState,
    hasRole,
  }
}
